import React from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { FileTextIcon, CalendarIcon, MailIcon, CopyIcon, PlayIcon, ScaleIcon, DollarSignIcon, ListIcon, ChevronRightIcon, UsersIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, CheckCircleIcon, CircleIcon, TagIcon, PlusIcon, ShieldIcon } from 'lucide-react';
import { ISSUE_COLORS } from '../components/review/types';
interface CaseDashboardScreenProps {
  onLogout: () => void;
  onNavigateCases: () => void;
  onStartReview: () => void;
  onNavigateMembers: () => void;
  onNavigateAdmin: () => void;
}
export function CaseDashboardScreen({
  onLogout,
  onNavigateCases,
  onStartReview,
  onNavigateMembers,
  onNavigateAdmin
}: CaseDashboardScreenProps) {
  // Mock stats for the dashboard
  const stats = {
    totalDocs: 2847,
    dateRange: 'Jul 10, 2023 — Sep 15, 2023',
    emailThreads: 142,
    duplicateGroups: 89
  };
  // Mock review status breakdown (mutually exclusive, adds to totalDocs)
  const reviewBreakdown = [{
    status: 'Hot',
    count: 47,
    color: 'bg-red-500',
    textColor: 'text-red-400',
    icon: FlameIcon
  }, {
    status: 'Critical',
    count: 124,
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    icon: AlertOctagonIcon
  }, {
    status: 'TBD',
    count: 38,
    color: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    icon: HelpCircleIcon
  }, {
    status: 'Reviewed',
    count: 683,
    color: 'bg-green-500',
    textColor: 'text-green-400',
    icon: CheckCircleIcon
  }, {
    status: 'Not Reviewed',
    count: 1955,
    color: 'bg-gray-600',
    textColor: 'text-gray-400',
    icon: CircleIcon
  }];
  const totalForBar = reviewBreakdown.reduce((sum, r) => sum + r.count, 0);
  const reviewedTotal = reviewBreakdown.filter(r => r.status !== 'Not Reviewed').reduce((sum, r) => sum + r.count, 0);
  const reviewPct = Math.round(reviewedTotal / totalForBar * 100);
  // Mock case issues
  const caseIssues = [{
    id: 'issue-1',
    label: 'Lease Agreement',
    color: ISSUE_COLORS[0],
    count: 342
  }, {
    id: 'issue-2',
    label: 'Zoning / Land Use',
    color: ISSUE_COLORS[1],
    count: 187
  }, {
    id: 'issue-3',
    label: 'Environmental',
    color: ISSUE_COLORS[2],
    count: 94
  }, {
    id: 'issue-4',
    label: 'Title Defect',
    color: ISSUE_COLORS[3],
    count: 56
  }, {
    id: 'issue-5',
    label: 'Financial Damages',
    color: ISSUE_COLORS[4],
    count: 218
  }];
  // Mock document type breakdown
  const typeBreakdown = [{
    type: 'Emails',
    count: 1842,
    color: 'bg-blue-500',
    width: '65%'
  }, {
    type: 'Contracts',
    count: 323,
    color: 'bg-green-500',
    width: '11%'
  }, {
    type: 'Reports',
    count: 412,
    color: 'bg-teal-500',
    width: '14%'
  }, {
    type: 'Invoices',
    count: 168,
    color: 'bg-amber',
    width: '6%'
  }, {
    type: 'Other',
    count: 102,
    color: 'bg-gray-500',
    width: '4%'
  }];
  return <div className="min-h-screen flex flex-col bg-navy" data-id="element-519">
      <Header caseName="Smith v. Acme Corp" documentCount={stats.totalDocs} onLogout={onLogout} onNavigateBack={onNavigateCases} data-id="element-520" />

      <main className="flex-1 overflow-y-auto p-8" data-id="element-521">
        <div className="max-w-6xl mx-auto space-y-10" data-id="element-522">
          {/* Header Section */}
          <div className="flex items-start justify-between" data-id="element-523">
            <div data-id="element-524">
              <h1 className="text-3xl font-serif text-white mb-2" data-id="element-525">
                Case Overview
              </h1>
              <p className="text-gray-400" data-id="element-526">
                High-level summary and starting points for Smith v. Acme Corp.
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-527">
              <Button variant="outline" onClick={onNavigateAdmin} data-id="element-528">
                <ShieldIcon className="w-4 h-4 mr-2" data-id="element-529" />
                Admin Panel
              </Button>
              <Button variant="outline" onClick={onNavigateMembers} data-id="element-530">
                <UsersIcon className="w-4 h-4 mr-2" data-id="element-531" />
                Team Members
              </Button>
            </div>
          </div>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-532">
            <Card className="p-5 flex flex-col justify-center" data-id="element-533">
              <div className="flex items-center gap-3 mb-2" data-id="element-534">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-535">
                  <FileTextIcon className="w-5 h-5" data-id="element-536" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-537">
                  Total Documents
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-538">
                {stats.totalDocs.toLocaleString()}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-539">
              <div className="flex items-center gap-3 mb-2" data-id="element-540">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-541">
                  <CalendarIcon className="w-5 h-5" data-id="element-542" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-543">
                  Date Range
                </span>
              </div>
              <div className="text-lg font-medium text-white mt-1 leading-tight" data-id="element-544">
                {stats.dateRange}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-545">
              <div className="flex items-center gap-3 mb-2" data-id="element-546">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-547">
                  <MailIcon className="w-5 h-5" data-id="element-548" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-549">
                  Email Threads
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-550">
                {stats.emailThreads.toLocaleString()}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-551">
              <div className="flex items-center gap-3 mb-2" data-id="element-552">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-553">
                  <CopyIcon className="w-5 h-5" data-id="element-554" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-555">
                  Duplicate Groups
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-556">
                {stats.duplicateGroups.toLocaleString()}
              </div>
            </Card>
          </div>

          {/* Document Type Breakdown */}
          <Card className="p-6" data-id="element-557">
            <h2 className="text-lg font-serif text-white mb-6" data-id="element-558">
              Document Types
            </h2>

            {/* Stacked Bar */}
            <div className="h-6 w-full flex rounded-full overflow-hidden mb-6 bg-navy-lighter" data-id="element-559">
              {typeBreakdown.map(item => <div key={item.type} className={`h-full ${item.color} border-r border-navy last:border-r-0 transition-all duration-500 hover:opacity-90`} style={{
              width: item.width
            }} title={`${item.type}: ${item.count.toLocaleString()}`} data-id="element-560" />)}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-8 gap-y-4" data-id="element-561">
              {typeBreakdown.map(item => <div key={item.type} className="flex items-center gap-2" data-id="element-562">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} data-id="element-563" />
                  <div data-id="element-564">
                    <span className="text-sm text-gray-300 font-medium mr-2" data-id="element-565">
                      {item.type}
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-566">
                      {item.count.toLocaleString()}
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Review Progress */}
          <Card className="p-6" data-id="element-567">
            <div className="flex items-center justify-between mb-6" data-id="element-568">
              <h2 className="text-lg font-serif text-white" data-id="element-569">Review Progress</h2>
              <div className="text-sm text-gray-400" data-id="element-570">
                <span className="text-white font-semibold" data-id="element-571">{reviewPct}%</span>{' '}
                reviewed
                <span className="text-gray-500 ml-1" data-id="element-572">
                  ({reviewedTotal.toLocaleString()} of{' '}
                  {totalForBar.toLocaleString()})
                </span>
              </div>
            </div>

            {/* Stacked Bar */}
            <div className="h-6 w-full flex rounded-full overflow-hidden mb-6 bg-navy-lighter" data-id="element-573">
              {reviewBreakdown.map(item => {
              const pct = item.count / totalForBar * 100;
              if (pct < 0.5) return null;
              return <div key={item.status} className={`h-full ${item.color} border-r border-navy/50 last:border-r-0 transition-all duration-500`} style={{
                width: `${pct}%`
              }} title={`${item.status}: ${item.count.toLocaleString()}`} data-id="element-574" />;
            })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-575">
              {reviewBreakdown.map(item => {
              const Icon = item.icon;
              const pct = (item.count / totalForBar * 100).toFixed(1);
              return <div key={item.status} className="flex items-center gap-2" data-id="element-576">
                    <Icon className={`w-3.5 h-3.5 ${item.textColor}`} data-id="element-577" />
                    <span className="text-sm text-gray-300 font-medium" data-id="element-578">
                      {item.status}
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-579">
                      {item.count.toLocaleString()}
                    </span>
                    <span className="text-[10px] text-gray-600" data-id="element-580">({pct}%)</span>
                  </div>;
            })}
            </div>
          </Card>

          {/* Case Issues */}
          <Card className="p-6" data-id="element-581">
            <div className="flex items-center justify-between mb-6" data-id="element-582">
              <div data-id="element-583">
                <h2 className="text-lg font-serif text-white flex items-center gap-2" data-id="element-584">
                  <TagIcon className="w-5 h-5 text-gray-400" data-id="element-585" />
                  Case Issues
                </h2>
                <p className="text-sm text-gray-400 mt-1" data-id="element-586">
                  Predefined tags available for reviewers to apply to documents.
                </p>
              </div>
              <Button variant="outline" className="gap-2" data-id="element-587">
                <PlusIcon className="w-4 h-4" data-id="element-588" />
                Add Issue
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-589">
              {caseIssues.map(issue => <div key={issue.id} className="flex items-center justify-between p-3 rounded-lg border border-navy-lighter bg-navy-light/50 hover:bg-navy-light transition-colors" data-id="element-590">
                  <div className="flex items-center gap-3" data-id="element-591">
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: issue.color
                }} data-id="element-592" />
                    <span className="text-sm font-medium text-gray-200" data-id="element-593">
                      {issue.label}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 bg-navy px-2 py-1 rounded-md border border-navy-lighter" data-id="element-594">
                    {issue.count} docs
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Quick Start Actions */}
          <div data-id="element-595">
            <h2 className="text-2xl font-serif text-white mb-6" data-id="element-596">Quick Start</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-597">
              {/* Action 1 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-598">
                <div className="p-6 flex-1" data-id="element-599">
                  <div className="flex items-start justify-between mb-4" data-id="element-600">
                    <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400 group-hover:bg-blue-500/20 transition-colors" data-id="element-601">
                      <PlayIcon className="w-6 h-6" data-id="element-602" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-603">
                      87 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-604">
                    Review terminal emails
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-605">
                    Start with the final emails in each thread. This is the most
                    efficient way to grasp the full conversation without reading
                    redundant replies.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-606">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-607" />
                </div>
              </Card>

              {/* Action 2 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-608">
                <div className="p-6 flex-1" data-id="element-609">
                  <div className="flex items-start justify-between mb-4" data-id="element-610">
                    <div className="p-3 bg-green-500/10 rounded-xl text-green-400 group-hover:bg-green-500/20 transition-colors" data-id="element-611">
                      <ScaleIcon className="w-6 h-6" data-id="element-612" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-613">
                      323 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-614">
                    Review legal agreements
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-615">
                    Focus exclusively on contracts, NDAs, and formal agreements.
                    Pre-filtered to show only document types classified as legal
                    agreements.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-616">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-617" />
                </div>
              </Card>

              {/* Action 3 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-618">
                <div className="p-6 flex-1" data-id="element-619">
                  <div className="flex items-start justify-between mb-4" data-id="element-620">
                    <div className="p-3 bg-amber/10 rounded-xl text-amber group-hover:bg-amber/20 transition-colors" data-id="element-621">
                      <DollarSignIcon className="w-6 h-6" data-id="element-622" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-623">
                      168 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-624">
                    Review flagged financials
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-625">
                    Invoices and financial reports that have been flagged by the
                    AI enrichment process for potential discrepancies or
                    required QA.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-626">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-627" />
                </div>
              </Card>

              {/* Action 4 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-628">
                <div className="p-6 flex-1" data-id="element-629">
                  <div className="flex items-start justify-between mb-4" data-id="element-630">
                    <div className="p-3 bg-gray-500/10 rounded-xl text-gray-400 group-hover:bg-gray-500/20 transition-colors" data-id="element-631">
                      <ListIcon className="w-6 h-6" data-id="element-632" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-633">
                      1,847 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-634">
                    Browse all documents
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-635">
                    Open the full document table with no filters applied.
                    Explore the entire production set using the advanced sidebar
                    filters.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-636">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-637" />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>;
}